import type { ReactNode } from 'react';
import { useRouter } from '@tanstack/react-router';
import { PageLoader } from '@customerio/pluma-components/react';
import Journeys from '@/pages/journeys';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { generatePath } from '@/services/router/generate-path';
import { MigrationRouteView } from '@/services/router/migration-route-view';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { useMigrationHandoffHref } from '@/services/router/use-migration-handoff-href';
import { useCurrentWorkspace } from '@/context/current-workspace';

const LOADING_LABEL = 'Loading…';

type JourneysWorkflowHandoffProps = {
  /** The campaign whose canvas this URL addresses, from the matched route's params. */
  campaignId: string;
};

type WorkflowBuilderRouteViewProps = JourneysWorkflowHandoffProps & {
  /** The migrated page, mounted only once the flag is on. */
  children: ReactNode;
};

type OverviewWorkflowRouteViewProps = {
  /** The migrated page, mounted only once the flag is on. */
  children: ReactNode;
};

/**
 * The legacy setup-flow canvas URL, `/journeys/:segment/:campaignId/setup/workflow/actions`, under
 * both the `campaigns` and `automations` spellings (both are live). Flag off → the embedded
 * journeys app still owns the canvas and renders here. Flag on → this route claims the URL purely
 * to forward it to the migrated route, so existing links keep working without being re-pointed.
 *
 * Only the setup URL is handled here. The overview URL addresses the same canvas for a campaign
 * past the draft state and is served in place by `OverviewWorkflowRouteView` below.
 */
export function JourneysWorkflowHandoff({ campaignId }: JourneysWorkflowHandoffProps) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.WorkflowBuilderMigration);
  const router = useRouter();
  const to = useMigrationHandoffHref(
    (workspaceId) =>
      router.buildLocation({
        to: '/workspaces/$workspaceId/automations/$campaignId/workflow',
        params: { workspaceId, campaignId },
      }).href,
  );

  if (!isMigrated) {
    return <Journeys />;
  }

  if (to == null) {
    return <PageLoader label={LOADING_LABEL} />;
  }

  return <SafeNavigate to={to} options={{ shouldReplaceHistoryEntry: true }} />;
}

/**
 * The migrated `/automations/:campaignId/workflow` URL. Flag on → this app owns the page. Flag off
 * → the URL has no legacy counterpart (the embedded app only serves the `journeys` forms), so hand
 * back to one it does serve. The exact inverse of `JourneysWorkflowHandoff`; the two can't loop,
 * because each fires in the opposite flag state.
 *
 * The hand-back targets the state-agnostic `/journeys/campaigns/:campaignId/workflow` address
 * rather than the setup one. This URL is held by campaigns in every state — the Workflow tab
 * addresses it for a running campaign too — and the setup address only serves drafts: the embedded
 * app bounces a non-draft off it to the campaign overview index, which is not the workflow page at
 * all. The state-agnostic address resolves to the right workflow page for either state. Since the
 * hand-back replaces history, landing on the wrong page would also be unrecoverable by going back.
 */
export function WorkflowBuilderRouteView({ campaignId, children }: WorkflowBuilderRouteViewProps) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.WorkflowBuilderMigration);
  // Built with `generatePath` rather than the router: this address is served by the embedded
  // app, so it is not in the TanStack route tree that `buildLocation` types against.
  const legacyHref = useMigrationHandoffHref((workspaceId) =>
    generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId/workflow', { workspaceId, campaignId }),
  );
  // Readiness gates on the resolved workspace, not the URL segment: the page's reads go through
  // `requireWorkspaceId`, which throws while the workspaces list is still loading.
  const workspace = useCurrentWorkspace();

  return (
    <MigrationRouteView isMigrated={isMigrated} legacyHref={legacyHref} isReady={workspace != null}>
      {children}
    </MigrationRouteView>
  );
}

/**
 * The overview Workflow tab's `journeys` URL, under both taxonomy spellings. Unlike the
 * canonical URL, this one has an embedded counterpart at the same address, so the flag-off
 * answer is simply to render the embedded host — no handoff, no redirect.
 */
export function OverviewWorkflowRouteView({ children }: OverviewWorkflowRouteViewProps) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.WorkflowBuilderMigration);
  // Readiness gates on the resolved workspace for the same reason as the canonical URL: the
  // page's reads go through `requireWorkspaceId`, which throws while the workspaces list loads.
  const workspace = useCurrentWorkspace();

  if (!isMigrated) {
    return <Journeys />;
  }

  if (workspace == null) {
    return <PageLoader label={LOADING_LABEL} />;
  }

  return children;
}
